//MYQuotasAdmin.jsx
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Banner } from "../../common/Banner";
import ViewSocioAdmin from "./AdminViews/ViewSocio";
import RegisterPaymentAdmin from "./AdminViews/RegisterPayment";
import EmailViewAdmin from "./AdminViews/Emails";
import MonthlyReports from "./AdminViews/MonthlyReports";
import Periodicty from "./AdminViews/Periodicity/Periodicity";
import SendQuotas from "./AdminViews/sendQuotas/SendQuotas";
import Swal from "sweetalert2";
import { getStatusPayments, updateStatusPayments } from "../../../services/settings.Service";
import { ActionLoader } from "../../loader";
import { healthCheck } from "../../../services/quotasService";

const options = [
  { key: "viewSocio", label: "🔍 Buscar Socio", description: "Buscar y visualizar información de socios" },
  { key: "monthlyReports", label: "📅 Reportes Mensuales", description: "Acceso a reportes mensuales de cuotas" },
  { key: "emails", label: "📤 Correos Enviados", description: "Historial de comunicaciones por email" },
  { key: "periodicity", label: "🔁 Cambio de Periodicidad", description: "Modificar frecuencia de pagos de socios" },
  { key: "sendQuotas", label: "💰 Envio de cuotas", description: "Enviar las cuotas sociales del periodo" }
];

const MyQuotasAdmin = () => {
  const [view, setView] = useState(null);
  const [paymentsEnabled, setPaymentsEnabled] = useState(true);
  const [webpayHealth, setWebpayHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const handleBack = () => setView(null);
  const [loadingPayments, setLoadingPayments] = useState(true);

  const renderView = () => {
    switch (view) {
      case "viewSocio":
        return <ViewSocioAdmin />;
      case "monthlyReports":
        return <MonthlyReports />;
      case "registerPayment":
        return <RegisterPaymentAdmin />;
      case "emails":
        return <EmailViewAdmin />;
      case "periodicity":
        return <Periodicty />;
      case "sendQuotas":
        return <SendQuotas />;
      default:
        return null;
    }
  };

  useEffect(() => {
    const loadPaymentsStatus = async () => {
      try {
        setLoadingPayments(true);
        const data = await getStatusPayments();
        setPaymentsEnabled(Boolean(data.enabled));
      } catch (error) {
        console.error("Error loading payments status", error);
        Swal.fire(
          "Error",
          "No se pudo obtener el estado de los pagos.",
          "error"
        );
      } finally {
        setLoadingPayments(false);
      }
    };

    loadPaymentsStatus();
  }, []);

  const handleTogglePayments = async () => {
    const nextValue = !paymentsEnabled;

    const result = await Swal.fire({
      title: nextValue ? "¿Habilitar pagos?" : "¿Deshabilitar pagos?",
      text: nextValue
        ? "Los socios podrán realizar pagos en el sistema."
        : "Los socios NO podrán realizar pagos en el sistema.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: nextValue ? "#16A34A" : "#DC2626",
      cancelButtonColor: "#6B7280",
      confirmButtonText: nextValue ? "Sí, habilitar" : "Sí, deshabilitar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      setLoadingPayments(true);
      await updateStatusPayments(nextValue);
      setPaymentsEnabled(nextValue);

      Swal.fire({
        icon: "success",
        title: "Estado actualizado",
        text: nextValue
          ? "Los pagos han sido habilitados."
          : "Los pagos han sido deshabilitados.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error updating payments status", error);
      Swal.fire(
        "Error",
        "No se pudo actualizar el estado de los pagos.",
        "error"
      );
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleCheckWebpayHealth = async () => {
    try {
      setLoadingHealth(true);
      
      // Ajusta esta URL a tu endpoint
      const response = await healthCheck();
      setWebpayHealth(response);

      // Mostrar resultado en modal
      const statusColor = response.ok ? 'success' : 'error';
      const statusIcon = response.ok ? 'success' : 'error';
      Swal.fire({
        title: response.ok ? '✅ Webpay Configurado' : '❌ Error en Webpay',
        html: `
          <div class="text-left space-y-2">
            <p><strong>Ambiente:</strong> <span class="font-mono ${response.env === 'PRODUCTION' ? 'text-green-600' : 'text-blue-600'}">${response.env}</span></p>
            <p><strong>Commerce Code:</strong> <span class="font-mono">${response.commerceCode || 'NO CONFIGURADO'}</span></p>
            <p><strong>API Key:</strong> <span class="font-mono">${response.apiKey || 'NO CONFIGURADO'}</span></p>
            <p><strong>Return URL:</strong> <span class="font-mono">${response.returnUrl || 'NO CONFIGURADO'}</span></p>
            <p><strong>Timeout:</strong> ${response.timeout_minutes} minutos</p>
            ${response.error ? `<p class="text-red-600 mt-3"><strong>Error:</strong> ${response.error}</p>` : ''}
          </div>
        `,
        icon: statusIcon,
        confirmButtonText: 'Entendido',
        confirmButtonColor: response.ok ? '#16A34A' : '#DC2626',
        width: '600px',
      });

    } catch (error) {
      console.error("Error checking Webpay health:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error de conexión',
        text: 'No se pudo verificar el estado de Webpay. Verifica la conexión con el servidor.',
        confirmButtonColor: '#DC2626',
      });
    } finally {
      setLoadingHealth(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 via-white to-rose-50 p-6">
      <ActionLoader loading={loadingPayments || loadingHealth} />
      <div className="max-w-7xl mx-auto">
        {view === null ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="space-y-8"
          >
            {/* Header */}
            <Banner title="Administración de Cuotas" message="Gestiona todos los aspectos relacionados con los socios y sus pagos desde un solo lugar" />
            
            {/* Global system status */}
            <div className="bg-white rounded-2xl p-5 shadow border border-gray-200 space-y-4">
              {/* Payments Toggle */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    Estado del sistema
                  </h3>
                  <p className="text-sm text-gray-500">
                    Control global de funcionalidades críticas
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-700">
                    Pagos
                  </span>

                  <button
                    onClick={handleTogglePayments}
                    className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors duration-300 ${
                      paymentsEnabled ? "bg-green-500" : "bg-red-500"
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform duration-300 ${
                        paymentsEnabled ? "translate-x-9" : "translate-x-1"
                      }`}
                    />
                  </button>

                  <span
                    className={`text-sm font-semibold ${
                      paymentsEnabled ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {paymentsEnabled ? "Habilitados" : "Deshabilitados"}
                  </span>
                </div>
              </div>

              {/* Webpay Health Check */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    Estado de Webpay
                  </h3>
                  <p className="text-sm text-gray-500">
                    Verifica la configuración y conexión con Transbank
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  {webpayHealth && (
                    <div className="flex items-center gap-2">
                      <div className={`h-3 w-3 rounded-full ${webpayHealth.ok ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className={`text-sm font-medium ${webpayHealth.ok ? 'text-green-600' : 'text-red-600'}`}>
                        {webpayHealth.env}
                      </span>
                    </div>
                  )}
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleCheckWebpayHealth}
                    disabled={loadingHealth}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#0488AF] to-[#0366A0] text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    <span className="text-lg">🔍</span>
                    {loadingHealth ? 'Verificando...' : 'Verificar Estado'}
                  </motion.button>
                </div>
              </div>
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {options.map((option, index) => (
                <motion.div
                  key={option.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: index * 0.1,
                    ease: "easeOut" 
                  }}
                  whileHover={{ 
                    y: -8,
                    transition: { duration: 0.2 }
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setView(option.key)}
                  className="group cursor-pointer"
                >
                  <div className="relative bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-2xl transition-all duration-300 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0488AF]/5 to-[#C42251]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    
                    <div className="relative z-10 space-y-4">
                      <div className="text-4xl mb-3">
                        {option.label.split(' ')[0]}
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 group-hover:text-[#0488AF] transition-colors duration-300">
                        {option.label.substring(2)}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {option.description}
                      </p>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0488AF] to-[#C42251] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-6"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleBack}
              className="inline-flex items-center gap-3 px-3 py-2 bg-[#DC3465] hover:bg-[#c02e59] border border-gray-300 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 text-white font-medium"
            >
              <span className="text-lg">⬅</span>
              Volver al menú principal
            </motion.button>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              {renderView()}
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default MyQuotasAdmin;